import {
  actions,
  callInSequence,
  ClearBehaviour,
  DropdownList,
  DropdownOptionValue,
  HM,
  KeyMap,
  msToStringValue,
  TextInputProps,
  useCallbackRef,
  useInputDropdown,
} from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import React, {
  PropsWithChildren,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FormControlViewModelType,
  NumericInputFormControlViewModel,
  TextInputFormControlViewModel,
  TimeInputFormControlViewModel,
} from '../../../../services/form/view-model/FormControlViewModel';
import UniqueId from '../../../../services/uid/UniqueId';
import { useAppDispatch } from '../../../../store/hooks';
import form from '../../../../store/slices/form';

interface Props extends PropsWithChildren {
  control:
    | TextInputFormControlViewModel
    | NumericInputFormControlViewModel
    | TimeInputFormControlViewModel;
  formId: UniqueId;
  onChange: (value: DropdownOptionValue) => void;
}

const HistoryContainer: React.FC<Props> = ({
  control,
  formId,
  onChange,
  children,
}) => {
  const dispatch = useAppDispatch();
  const [showHistory, setShowHistory] = useState(false);

  // TODO: refactor, as placement should be taken from floating-ui and be dynamic
  const placement = 'bottom';
  const { inputClasses, dropdownClasses } = useInputDropdown(
    placement,
    showHistory
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = (v: DropdownOptionValue) => {
    onChange(v);
    setShowHistory(false);
  };

  const keymap = useMemo(
    () => ({ [KeyMap[actions.EXPAND_CONTROL]]: () => setShowHistory(true) }),
    []
  );

  const options = useMemo(
    () =>
      control.history?.map(value => ({
        type: 'option' as const,
        value,
        label:
          control.type === FormControlViewModelType.TIME_INPUT
            ? msToStringValue(Number(value), HM)
            : value.toString(),
      })) ?? [],
    [control.history, control.type]
  );

  useEffect(() => {
    const handleClick = () => {
      if (showHistory) setShowHistory(false);
    };

    const container = containerRef.current;
    container?.addEventListener('click', handleClick);

    return () => {
      container?.removeEventListener('click', handleClick);
    };
  }, [showHistory]);

  useEffect(() => {
    const handleDoubleClick = (e: MouseEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName))
        setShowHistory(true);
    };

    const container = containerRef.current;
    container?.addEventListener('dblclick', handleDoubleClick);

    return () => {
      container?.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  const containerCallbackRef = useCallbackRef(
    React.isValidElement(children) && children?.props.onRef,
    containerRef
  );

  const handleRemove = (_value: DropdownOptionValue, index: number) => {
    dispatch(form.removeHistoryItem({ formId, field: control.name, index }));
  };

  return (
    <>
      {React.isValidElement(children) &&
        React.cloneElement(React.Children.only(children), {
          ...children.props,
          className: classNames(children.props.className, inputClasses),
          onRef: containerCallbackRef,
          keymap,
        })}
      <DropdownList
        className={classNames('overflow-hidden', dropdownClasses)}
        open={showHistory}
        options={options}
        placement={placement}
        referenceElement={containerRef.current}
        withTypeahead
        withReferenceWidth
        removable={ClearBehaviour.BOTH}
        onClick={handleChange}
        onOpenChange={setShowHistory}
        onRemoveItem={handleRemove}
      />
    </>
  );
};

export interface RequiredHistoryProps<
  T extends HTMLElement = HTMLDivElement,
> extends Pick<TextInputProps, 'rounded'> {
  onRef: RefObject<T>;
}

export const withHistory =
  <P extends RequiredHistoryProps>(Component: React.ComponentType<P>) =>
  // eslint-disable-next-line react/display-name
  ({
    control,
    formId,
    onChange,
    onChanged,
    children,
    ...props
  }: (Props & P) | any): React.JSX.Element => {
    const component = (
      <Component
        control={control}
        formId={formId}
        onChange={onChange}
        onChanged={onChanged}
        {...props}
      />
    );

    return control.hasHistory &&
      control.history &&
      control.history.length > 0 &&
      !control.readonly &&
      !control.disabled ? (
      <HistoryContainer
        formId={formId}
        onChange={callInSequence(onChange, onChanged)}
        control={control}
      >
        {component}
      </HistoryContainer>
    ) : (
      <>{component}</>
    );
  };
